import useFocusTrap from '@charlietango/use-focus-trap';
import React, { FC, useCallback, useLayoutEffect } from 'react';
import styled from 'styled-components';

import { noop } from '#/service/utils';

import { Logo, LogoContainer } from '../Logo';
import { ReactPortal } from '../Portal';
import { FullScreenBackground } from './styled';

interface Props {
  /** @optional Background image URL */
  backgroundImageUrl?: string;

  /** @default false Shows a standard logo component on the overlay */
  showLogo?: boolean;

  /** @default false If `true`, the modal will close when the `Esc` key is pressed */
  closeOnEsc?: boolean;

  onClose?: () => void;
}

/**
 * This is a full page modal. You can use it when you have a design that hides all other parts of the application (i.e. menu, footer, etc)
 * It's not a perfect solution but for now it works fine. At some point we should replace it with something more convenient and accessibility-friendly
 */
export const FullPageModal: FC<Props> = ({
  children,
  backgroundImageUrl,
  showLogo,
  onClose = noop,
  closeOnEsc = false,
  ...rest
}) => {
  const ref = useFocusTrap();

  useLayoutEffect(() => {
    // We have to actually remove the body scrollbar here for aesthetic reasons
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const onKeyDown = useCallback(
    (ev: React.KeyboardEvent) => {
      if (ev.key === 'Escape' && closeOnEsc && onClose) {
        ev.stopPropagation();
        onClose();
      }
    },
    [closeOnEsc, onClose]
  );

  return (
    <ReactPortal>
      <StyledOverlay
        data-nosnippet
        data-testid="fullPageModal-backdrop"
        onKeyDown={onKeyDown}
        ref={ref}
      >
        {backgroundImageUrl && (
          <BackContainer>
            <BackImage src={backgroundImageUrl} />
          </BackContainer>
        )}
        <ModalBody data-testid="fullPageModal-body" role="dialog" {...rest}>
          {children}
        </ModalBody>
        {showLogo && (
          <LogoContainer>
            <Logo />
          </LogoContainer>
        )}
      </StyledOverlay>
    </ReactPortal>
  );
};

export const BackContainer = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  z-index: -1;
`;

export const BackImage = styled.img`
  mask-image: linear-gradient(to top, transparent, var(--pageBackground));
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const StyledOverlay = styled(FullScreenBackground)`
  background-color: var(--pageBackground);
  overflow-x: hidden;
`;

const ModalBody = styled.div`
  display: flex;
  justify-content: center;
  min-width: 100vw;
  height: 100vh;
  color: var(--pageParagraphForeground);
  text-align: center;
  position: relative;
  align-items: flex-start;
`;
